import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import PublicOfferView from '@/components/PublicOfferView';
import { InsuranceAnalysisResult } from '@/types/insurance';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const PublicOfferViewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const offerId = params.id || searchParams.get('id');
  const { toast } = useToast();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offerData, setOfferData] = useState<{
    title: string;
    fileUrl?: string | null;
    analysisResult?: InsuranceAnalysisResult | null;
    fileId?: string;
    companyName?: string;
    companyLogo?: string | null;
    offerContent?: string | null;
  } | null>(null);
  const [generationAttempted, setGenerationAttempted] = useState(false);

  useEffect(() => {
    async function fetchOfferData() {
      if (!offerId) {
        setError('Keine Angebots-ID gefunden');
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching offer with sharing ID:", offerId);
        
        // Get offer data
        const { data: offer, error: offerError } = await supabase
          .from('insurance_offers')
          .select(`
            id,
            title,
            file_id,
            analysis_result,
            user_id,
            status,
            offer_content
          `)
          .eq('sharing_id', offerId)
          .single();

        if (offerError || !offer) {
          console.error('Error fetching offer:', offerError);
          setError('Angebot nicht gefunden');
          setIsLoading(false);
          return;
        }

        console.log("Offer status:", offer.status);
        console.log("Has offer content:", !!offer.offer_content);
        
        // If offer content exists but status is still processing, set it to completed
        if (offer.status === 'processing' && offer.offer_content) {
          // Update the status to completed
          const { error: updateError } = await supabase
            .from('insurance_offers')
            .update({ status: 'completed' })
            .eq('id', offer.id);
          
          if (updateError) {
            console.error('Error updating offer status:', updateError);
          } else {
            console.log('Updated offer status to completed');
            offer.status = 'completed';
          }
        }
        
        // If still in processing status and no content, attempt to generate
        if (offer.status === 'processing' && !generationAttempted) {
          setGenerationAttempted(true);
          console.log('Offer is still being processed, attempting to generate content');
          
          try {
            // Get file data for generation
            const { data: fileData, error: fileError } = await supabase
              .from('files')
              .select('*')
              .eq('id', offer.file_id)
              .single();
              
            if (fileError) {
              console.error('Error fetching file for generation:', fileError);
              throw new Error('File data not found');
            }
            
            // Get company info for the offer
            const { data: companyData } = await supabase
              .from('company_settings')
              .select('*')
              .eq('user_id', offer.user_id)
              .maybeSingle();

            // Show toast that generation is in progress
            toast({
              title: "Angebotsseite wird generiert",
              description: "Bitte haben Sie einen Moment Geduld...",
              duration: 5000,
            });
            
            // Call generate-offer-page function to complete the processing
            const { data: generationResult, error: generationError } = await supabase.functions.invoke('generate-offer-page', {
              body: {
                offerId: offer.id,
                companyInfo: companyData,
                includeSignature: false
              }
            });
            
            if (generationError) {
              console.error('Error generating offer page:', generationError);
              throw new Error('Error generating offer content');
            }
            
            console.log('Generated offer content, refreshing offer data');
            
            // Fetch the updated offer
            const { data: updatedOffer, error: refreshError } = await supabase
              .from('insurance_offers')
              .select(`
                id,
                title,
                file_id,
                analysis_result,
                user_id,
                status,
                offer_content
              `)
              .eq('id', offer.id)
              .single();
              
            if (!refreshError && updatedOffer) {
              // Use the updated offer data
              offer.status = updatedOffer.status;
              offer.offer_content = updatedOffer.offer_content;
              
              toast({
                title: "Angebotsseite generiert",
                description: "Die Seite wurde erfolgreich erstellt.",
                variant: "default",
              });
            }
          } catch (genError) {
            console.error('Error generating offer content:', genError);
            toast({
              title: "Fehler",
              description: "Beim Generieren der Angebotsseite ist ein Fehler aufgetreten.",
              variant: "destructive",
            });
            // Continue with what we have
          }
        }
        
        // Still in processing status after generation attempt
        if (offer.status === 'processing') {
          console.log('Offer is still being processed after generation attempt');
          setError('Angebot wird noch verarbeitet. Bitte versuchen Sie es später erneut.');
          setIsLoading(false);
          return;
        }

        // Get file data
        const { data: file, error: fileError } = await supabase
          .from('files')
          .select('*')
          .eq('id', offer.file_id)
          .single();

        if (fileError) {
          console.error('Error fetching file:', fileError);
          setError('Datei nicht gefunden');
          setIsLoading(false);
          return;
        }

        // Get file URL
        const { data: urlData } = supabase
          .storage
          .from(file.bucket_id)
          .getPublicUrl(file.storage_path);

        // Get company info
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('company_name, avatar_url')
          .eq('id', offer.user_id)
          .maybeSingle();

        // Make sure we have valid profile data
        const companyName = profileError ? undefined : profile?.company_name;
        const companyLogo = profileError ? null : profile?.avatar_url;
        
        // Also try to get company_settings for more detailed branding info
        const { data: companySettings } = await supabase
          .from('company_settings')
          .select('*')
          .eq('user_id', offer.user_id)
          .maybeSingle();
        
        // Use company settings data if available (will have more branding details)
        const finalCompanyName = companySettings?.company_name || companyName;
        const finalCompanyLogo = companySettings?.logo_url || companyLogo;

        setOfferData({
          title: offer.title || file.filename,
          fileUrl: urlData.publicUrl,
          analysisResult: offer.analysis_result as unknown as InsuranceAnalysisResult,
          fileId: offer.file_id,
          companyName: finalCompanyName,
          companyLogo: finalCompanyLogo,
          offerContent: offer.offer_content
        });
      } catch (error) {
        console.error('Error:', error);
        setError('Ein Fehler ist aufgetreten');
      } finally {
        setIsLoading(false);
      }
    }

    fetchOfferData();
  }, [offerId, generationAttempted, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-700">Angebot wird geladen...</h2>
        </div>
      </div>
    );
  }

  if (error || !offerData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Fehler</h2>
          <p className="text-gray-600">{error || 'Ein unbekannter Fehler ist aufgetreten'}</p>
          {error && error.includes('verarbeitet') && (
            <div className="mt-4">
              <p className="text-sm text-gray-500 mb-3">
                Das Angebot wird noch erstellt. Bitte laden Sie die Seite in einigen Momenten neu.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                Seite neu laden
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <PublicOfferView
      title={offerData.title}
      fileUrl={offerData.fileUrl}
      analysisResult={offerData.analysisResult}
      offerType="offer"
      fileId={offerData.fileId}
      companyName={offerData.companyName}
      companyLogo={offerData.companyLogo}
      offerContent={offerData.offerContent}
    />
  );
};

export default PublicOfferViewPage;
